"use client";

import * as React from "react";
import { ChevronDown, Compass, Target } from "lucide-react";

import { Reveal } from "@/components/site/sections/overview-board/reveal";
import {
  RISK_TONES,
  SIGNAL_SOURCES,
} from "@/components/site/sections/overview-board/styles";
import { MentionText } from "@/components/site/sections/six-pillars-preview/shared-surface";
import type { OverviewDocument } from "@/lib/overview-document";
import { cn } from "@/lib/utils";

const CONFIDENCE_STEPS = { low: 1, medium: 2, high: 3 } as const;
const CONFIDENCE_LABELS = { low: "Low", medium: "Medium", high: "High" } as const;

export function RiskHeader({
  document,
  headingLevel,
  size,
}: {
  document: OverviewDocument;
  headingLevel: 2 | 3;
  size: "compact" | "expanded";
}) {
  const risk = document.currentRisk;
  const detailsId = React.useId();
  const [open, setOpen] = React.useState(false);
  const Heading = `h${headingLevel}` as const;

  return (
    <section className="flex flex-col gap-3">
      <div className="relative flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "inline-flex h-[22px] items-center gap-[5px] rounded-md border px-2 text-xs font-medium",
            RISK_TONES[risk.level],
          )}
        >
          <Compass className="size-[13px]" aria-hidden="true" />
          {risk.levelLabel}
        </span>
        <GoalReference goals={document.goals} asOf={document.generatedOn} />
      </div>

      <Heading
        className={cn(
          "max-w-[56rem] font-semibold text-balance text-foreground",
          size === "compact"
            ? "text-[1.125rem] leading-[1.32] tracking-[-0.018em] sm:text-[1.5rem] sm:leading-[1.25] sm:tracking-[-0.022em]"
            : "text-[1.375rem] leading-[1.24] tracking-[-0.026em] sm:text-[1.875rem] sm:leading-[1.22]",
        )}
      >
        {risk.headline}{" "}
        <span className="text-foreground/50">{risk.headlineReason}</span>
      </Heading>

      <button
        type="button"
        aria-expanded={open}
        aria-controls={detailsId}
        onClick={() => setOpen((current) => !current)}
        className="-ml-2 inline-flex h-8 items-center gap-1.5 self-start rounded-md px-2 text-sm font-medium text-foreground/80 transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        {open ? "Hide risk details" : "Show risk details"}
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "size-4 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </button>

      <Reveal id={detailsId} open={open} className="-mt-3">
        <div className="pt-3">
          <RiskDetails document={document} />
        </div>
      </Reveal>
    </section>
  );
}

/**
 * The goals the risk was measured against, as a chip beside the risk level.
 * Goals can be long, so the text as of the snapshot opens on demand.
 */
function GoalReference({ goals, asOf }: { goals: string[]; asOf: string }) {
  const panelId = React.useId();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [left, setLeft] = React.useState(0);

  // The panel hangs from the chip but never past the row's right edge, so it stays inside narrow previews.
  React.useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    const row = panel?.offsetParent as HTMLElement | null;

    if (!open || !trigger || !panel || !row) {
      return;
    }

    const place = () =>
      setLeft(
        Math.max(
          0,
          Math.min(trigger.offsetLeft, row.clientWidth - panel.offsetWidth),
        ),
      );

    place();
    const observer = new ResizeObserver(place);
    observer.observe(row);

    return () => observer.disconnect();
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const closeOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    window.addEventListener("pointerdown", closeOutside);

    return () => window.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  if (goals.length === 0) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          // The fullscreen preview also closes on Escape; this one belongs to the popover.
          event.stopPropagation();
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          "inline-flex h-[22px] items-center gap-1.5 rounded-md border bg-card pr-1.5 pl-2 text-xs font-medium text-foreground/80 transition-colors hover:border-foreground/35 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
          open && "border-foreground/35 text-foreground",
        )}
      >
        <Target className="size-3" aria-hidden="true" />
        Team goals
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "size-3 opacity-60 transition-transform duration-300 motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </button>

      <div
        ref={panelRef}
        id={panelId}
        role="region"
        aria-label={`Team goals as of ${asOf}`}
        className={cn(
          "absolute top-full z-20 mt-1.5 flex w-[min(22rem,100%)] flex-col rounded-xl border bg-popover text-popover-foreground shadow-[0_18px_40px_-22px_rgba(15,23,42,0.32)] transition-[opacity,transform,visibility] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transform-none motion-reduce:transition-opacity",
          open
            ? "visible translate-y-0 opacity-100"
            : "pointer-events-none invisible -translate-y-1 opacity-0",
        )}
        style={{ left }}
      >
        <div className="flex items-center gap-2 border-b px-4 pt-3.5 pb-3">
          <span className="inline-flex size-7 flex-none items-center justify-center rounded-full bg-muted">
            <Target className="size-3.5 text-foreground/80" aria-hidden="true" />
          </span>
          <span className="text-sm font-semibold">Team goals as of {asOf}</span>
        </div>
        <ul className="flex flex-col gap-1.5 px-4 py-3">
          {goals.map((goal) => (
            <li
              key={goal}
              className="flex gap-2 text-sm leading-relaxed text-foreground/85"
            >
              <span
                aria-hidden="true"
                className="mt-[9px] size-[5px] flex-none rounded-full bg-foreground/35"
              />
              <span className="min-w-0">{goal}</span>
            </li>
          ))}
        </ul>
        <p className="rounded-b-xl border-t bg-muted/40 px-4 py-2.5 text-xs leading-snug text-muted-foreground">
          The overview measured the risk against these goals. Changed goals go
          into the next overview.
        </p>
      </div>
    </div>
  );
}

function DetailLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-semibold text-muted-foreground">
      {children}
    </span>
  );
}

function RiskDetails({ document }: { document: OverviewDocument }) {
  const risk = document.currentRisk;
  const steps = CONFIDENCE_STEPS[risk.confidence];

  return (
    <div className="@container overflow-hidden rounded-2xl border bg-card">
      <div className="grid @min-[44rem]:grid-cols-[3fr_2fr]">
        <div className="flex min-w-0 flex-col gap-5 px-5 py-5 sm:px-6 sm:py-6">
          <MentionText
            text={risk.rationale}
            className="max-w-[70ch] text-[0.9375rem] leading-[1.75] text-foreground/90"
          />

          {risk.limitations.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              <DetailLabel>What could skew this conclusion</DetailLabel>
              <ul className="flex flex-col gap-2">
                {risk.limitations.map((limitation) => (
                  <li
                    key={limitation}
                    className="flex gap-2.5 text-sm leading-relaxed text-foreground/80"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-[9px] size-[5px] flex-none rounded-full bg-foreground/35"
                    />
                    <span className="min-w-0">{limitation}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-5 border-t bg-muted/40 px-5 py-5 sm:px-6 sm:py-6 @min-[44rem]:border-t-0 @min-[44rem]:border-l">
          <div className="flex flex-col gap-2">
            <DetailLabel>Confidence</DetailLabel>
            <span className="flex items-center gap-2.5">
              <span
                role="img"
                aria-label={`${CONFIDENCE_LABELS[risk.confidence]}: ${steps} of 3`}
                className="inline-flex gap-[3px]"
              >
                {[1, 2, 3].map((step) => (
                  <span
                    key={step}
                    className={cn(
                      "h-1.5 w-[18px] rounded-full",
                      step <= steps ? "bg-foreground" : "bg-foreground/15",
                    )}
                  />
                ))}
              </span>
              <span aria-hidden="true" className="text-sm font-semibold">
                {CONFIDENCE_LABELS[risk.confidence]}
              </span>
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <DetailLabel>What this is based on</DetailLabel>
            <ul className="flex flex-wrap gap-1.5">
              {risk.sources.map((source) => {
                const { icon: Icon, label } = SIGNAL_SOURCES[source];

                return (
                  <li
                    key={source}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-background py-1 pr-2.5 pl-2 text-[0.8125rem] font-medium"
                  >
                    <Icon className="size-3.5 text-foreground/70" aria-hidden="true" />
                    {label}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
